import { useEffect, useRef } from 'react';

/**
 * Generates a UUID using `crypto.getRandomValues` for uniqueness.
 * This ID is used to uniquely identify each browser tab.
 */
export function generateUUID() {
  return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  );
}

/**
 * Custom hook to track browser tabs of the same origin.
 * 
 * It stores a unique tab ID in sessionStorage and keeps a list of open tabs in localStorage.
 * On tab close (including reload or navigation), it removes that tab from the list.
 * If the last tab is closed, it clears localStorage and sessionStorage.
 * 
 * @param {Function} onTabsUpdate - Callback that receives the updated list of tab IDs when it changes.
 */
export function useTabTracker(onTabsUpdate) {
  // Unique ID for the current tab instance
  const tabId = useRef(generateUUID());

  // Keys for storage
  const TAB_LIST_KEY = 'openTabs';       // localStorage key for storing all open tab IDs
  const TAB_ID_KEY = 'currentTabId';     // sessionStorage key for the current tab ID

  /**
   * Fetch the list of tab IDs from localStorage.
   * @returns {string[]} Array of tab IDs
   */
  const getTabs = () => JSON.parse(localStorage.getItem(TAB_LIST_KEY) || '[]');

  /**
   * Add or remove this tab's ID to/from the list of open tabs in localStorage.
   * @param {boolean} add - If true, add the tab; if false, remove it.
   */
  const updateTabs = (add = true) => {
    const tabs = getTabs();
    const updatedTabs = add
      ? [...new Set([...tabs, tabId.current])] // ensure no duplicates
      : tabs.filter(id => id !== tabId.current); // remove current tab ID
    localStorage.setItem(TAB_LIST_KEY, JSON.stringify(updatedTabs));
  };

  useEffect(() => {
    // Save the current tab ID to sessionStorage
    sessionStorage.setItem(TAB_ID_KEY, tabId.current);

    // Add this tab ID to the global tab list
    updateTabs(true);

    /**
     * Handler for tab unload (close or reload).
     * It removes the tab from the open list, and clears storage if this was the last tab.
     */
    const onUnload = () => {
      let tabs = getTabs();
      tabs = tabs.filter(id => id !== tabId.current);

      if (tabs.length < 0) {
        // All tabs closed → clear all storage
        localStorage.clear();
        sessionStorage.clear();
      } else {
        // Update tab list with remaining tabs
        localStorage.setItem(TAB_LIST_KEY, JSON.stringify(tabs));
      }
    };

    // Listen for tab/window unload
    window.addEventListener('unload', onUnload);

    // Cleanup on unmount
    return () => {
      onUnload(); // manually trigger for safe cleanup
      window.removeEventListener('unload', onUnload);
    };
  }, []);

  useEffect(() => {
    /**
     * Listen for storage changes across tabs.
     * Triggers onTabsUpdate callback if the tab list changes.
     */
    const handleStorage = (event) => {
      if (event.key === TAB_LIST_KEY) {
        const tabs = JSON.parse(event.newValue || '[]');
        if (typeof onTabsUpdate === 'function') {
          onTabsUpdate(tabs);
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [onTabsUpdate]);
}
